import { useState } from "react";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import type { TestRun, RunStep, ExpectationResult } from "@/lib/types/test";

// Utility functions
const formatDate = (dateString: string) => {
  if (!dateString) return 'Unknown date';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'Invalid date';
  return date.toLocaleString();
};

// Run Data Modal Component (separate from details)
interface RunDataModalProps {
  run: TestRun;
  onClose: () => void;
}

export function RunDataModal({ run, onClose }: RunDataModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-popover rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/50">
          <h3 className="text-lg font-semibold">Run Data</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-auto max-h-[60vh]">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-muted/50">
                  <th className="border px-3 py-2 text-left text-sm font-medium text-muted-foreground">Key</th>
                  <th className="border px-3 py-2 text-left text-sm font-medium text-muted-foreground">Value</th>
                </tr>
              </thead>
              <tbody>
                {run.data && Object.keys(run.data).length > 0 ? (
                  Object.entries(run.data).map(([key, value]) => (
                    <tr key={key} className="hover:bg-accent">
                      <td className="border px-3 py-2 text-sm font-medium">{key}</td>
                      <td className="border px-3 py-2 text-sm text-muted-foreground">
                        {Array.isArray(value) ? (
                          <div className="space-y-1">
                            {value.map((item, index) => (
                              <div key={`item-${index}-${String(item).substring(0, 10)}`} className="text-sm">{typeof item === 'object' ? JSON.stringify(item) : item}</div>
                            ))}
                          </div>
                        ) : typeof value === 'object' ? (
                          <pre className="text-xs bg-muted p-2 rounded font-mono">{JSON.stringify(value, null, 2)}</pre>
                        ) : (
                          <span className="text-sm">{String(value)}</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2} className="border px-3 py-6 text-center text-sm text-muted-foreground">
                      No run data available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
}

// Step Details Modal Component
interface StepDetailsModalProps {
  step: RunStep;
  onClose: () => void;
}

export function StepDetailsModal({ step, onClose }: StepDetailsModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-popover rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/50">
          <h3 className="text-lg font-semibold">Step Details</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-auto max-h-96">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Name</span>
              <span className="text-sm font-medium">{step.name || `Step ${step.number}`}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Type</span>
              <span className="text-sm text-muted-foreground font-mono">{step.type}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Started At</span>
              <span className="text-sm text-muted-foreground">{step.startedAt ? formatDate(step.startedAt) : 'Not started'}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Finished At</span>
              <span className="text-sm text-muted-foreground">{step.finishedAt ? formatDate(step.finishedAt) : 'Not finished'}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">State</span>
              <span className={`text-sm px-2 py-1 rounded-full text-xs font-medium ${
                step.state === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                step.state === 'FAILURE' ? 'bg-red-100 text-red-800' :
                step.state === 'RUNNING' ? 'bg-blue-100 text-blue-800' :
                step.state === 'INIT' ? 'bg-gray-100 text-gray-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>{step.state}</span>
            </div>
            <div className="flex items-start justify-between py-3">
              <span className="text-sm font-medium text-muted-foreground">Log</span>
              <div className="text-right max-w-md">
                {step.log ? (
                  <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md font-mono whitespace-pre-wrap">
                    {step.log}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground italic">No log available</span>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

// Step Data Modal Component
interface StepDataModalProps {
  step: RunStep;
  onClose: () => void;
  showJson: boolean;
  onToggleJson: () => void;
}

export function StepDataModal({ step, onClose, showJson, onToggleJson }: StepDataModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-popover rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[80vh] overflow-hidden border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/50">
          <h3 className="text-lg font-semibold">Step Data</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-auto max-h-[60vh]">
          {/* Step Configuration */}
          <div className="mb-8">
            <h4 className="text-base font-semibold mb-4">Step Configuration</h4>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="border px-3 py-2 text-left text-sm font-medium text-muted-foreground">Key</th>
                    <th className="border px-3 py-2 text-left text-sm font-medium text-muted-foreground">Value</th>
                  </tr>
                </thead>
                <tbody>
                  {step.data && Object.keys(step.data).length > 0 ? (
                    Object.entries(step.data).map(([key, value]) => (
                      <tr key={key} className="hover:bg-accent">
                        <td className="border px-3 py-2 text-sm font-medium">{key}</td>
                        <td className="border px-3 py-2 text-sm text-muted-foreground">
                          {Array.isArray(value) ? (
                            <div className="space-y-1">
                              {value.map((item, index) => (
                                <div key={`item-${index}-${String(item).substring(0, 10)}`} className="text-sm">{typeof item === 'object' ? JSON.stringify(item) : item}</div>
                              ))}
                            </div>
                          ) : typeof value === 'object' ? (
                            <pre className="text-xs bg-muted p-2 rounded font-mono">{JSON.stringify(value, null, 2)}</pre>
                          ) : (
                            <span className="text-sm">{String(value)}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={2} className="border px-3 py-6 text-center text-sm text-muted-foreground">
                        No configuration data available
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Step Results */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-base font-semibold">Step Results</h4>
              {step.result && (
                <Button 
                  onClick={onToggleJson}
                  variant="secondary"
                  size="sm"
                  className="text-xs"
                >
                  {showJson ? 'View as Table' : 'View as JSON'}
                </Button>
              )}
            </div>
            
            {step.result && Object.keys(step.result).length > 0 ? (
              showJson ? (
                <div className="bg-muted p-4 rounded-md border">
                  <pre className="font-mono text-xs overflow-x-auto whitespace-pre-wrap">
                    {JSON.stringify(step.result, null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="border px-3 py-2 text-left text-sm font-medium text-muted-foreground">Key</th>
                        <th className="border px-3 py-2 text-left text-sm font-medium text-muted-foreground">Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(step.result).map(([key, value]) => (
                        <tr key={key} className="hover:bg-accent">
                          <td className="border px-3 py-2 text-sm font-medium">{key}</td>
                          <td className="border px-3 py-2 text-sm text-muted-foreground">
                            {Array.isArray(value) ? (
                              <div className="space-y-1">
                                {value.map((item, index) => (
                                  <div key={`item-${index}-${String(item).substring(0, 10)}`} className="text-sm">{typeof item === 'object' ? JSON.stringify(item) : item}</div>
                                ))}
                              </div>
                            ) : typeof value === 'object' ? (
                              <pre className="text-xs bg-muted p-2 rounded font-mono">{JSON.stringify(value, null, 2)}</pre>
                            ) : (
                              <span className="text-sm">{String(value)}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : (
              <div className="text-center py-8 text-sm text-muted-foreground bg-muted/50 rounded-md border border-dashed">
                No step results available
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}

// Expectation Data Modal Component
interface ExpectationDataModalProps {
  expectation: ExpectationResult;
  onClose: () => void;
}

interface ProcessedExpectationData {
  Name: string;
  Records: any[];
}

export function ExpectationDataModal({ expectation, onClose }: ExpectationDataModalProps) {
  const [expandedSheets, setExpandedSheets] = useState<Record<string, boolean>>({});
  const [jsonView, setJsonView] = useState(false);

  // Process expectation data like the original frontend
  const getExpectationData = (expectation: ExpectationResult): ProcessedExpectationData[] => {
    if (!expectation.data) return [];
    
    return expectation.data.map((data, index) => {
      let records: any[] = [];
      let name = '';
      
      if (data.Records) {
        records = data.Records;
        name = data.SheetName || `Sheet ${index + 1}`;
      } else if (data.Actions) {
        records = data.Actions;
        name = data.WorkbookName || `Workbook ${index + 1}`;
      } else if (data.Config) {
        records = data.Config;
        name = data.SheetName || `Config ${index + 1}`;
      } else if (data.mappingRules) {
        records = data.mappingRules;
        name = "Mapping Rules";
      } else {
        // Fallback - treat the data object itself as records
        records = [data];
        name = `Data ${index + 1}`;
      }
      
      return {
        Name: name,
        Records: Array.isArray(records) ? records : [records],
      };
    });
  };

  // Get sheet headers dynamically from data
  const getSheetHeaders = (sheetData: ProcessedExpectationData): string[] => {
    if (!sheetData.Records || !sheetData.Records.length) return [];
    return Object.keys(sheetData.Records[0]);
  };

  const toggleSheetExpansion = (sheetName: string) => {
    setExpandedSheets(prev => ({
      ...prev,
      [sheetName]: !prev[sheetName]
    }));
  };

  const processedData = getExpectationData(expectation);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-popover rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[80vh] overflow-hidden border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/50">
          <h3 className="text-lg font-semibold">Expectation Data</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-auto max-h-[60vh]">
          {/* Validation Reasoning */}
          {expectation.log && (
            <div className="mb-6 p-4 bg-muted/50 rounded-lg">
              <div className="text-sm font-medium text-muted-foreground mb-2">Validation Reasoning</div>
              <div className="text-sm">{expectation.log}</div>
            </div>
          )}

          {/* Data Preview Section */}
          {processedData.length > 0 ? (
            <div className="space-y-4">
              {processedData.map((sheetData) => (
                <div key={sheetData.Name} className="border rounded-lg overflow-hidden">
                  {/* Sheet Header */}
                  <div 
                    className="flex items-center justify-between p-4 bg-muted/30 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => toggleSheetExpansion(sheetData.Name)}
                  >
                    <div className="font-medium text-sm">{sheetData.Name}</div>
                    <span className="text-sm text-muted-foreground">
                      {expandedSheets[sheetData.Name] ? '▼' : '▶'}
                    </span>
                  </div>

                  {/* Sheet Content */}
                  {expandedSheets[sheetData.Name] && (
                    <div className="p-4 border-t">
                      {/* Data Section */}
                      {sheetData.Records && sheetData.Records.length > 0 && (
                        <div className="space-y-4">
                          {/* Section Header with Toggle */}
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium text-sm">Data Preview</h4>
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setJsonView(!jsonView)}
                              className="text-xs"
                            >
                              {jsonView ? 'View as Table' : 'View as JSON'}
                            </Button>
                          </div>

                          {/* Data Content */}
                          {jsonView ? (
                            <pre className="bg-muted p-4 rounded-md text-xs overflow-x-auto font-mono">
                              {JSON.stringify(sheetData.Records, null, 2)}
                            </pre>
                          ) : (
                            <div className="overflow-x-auto border rounded-md">
                              <table className="border-collapse" style={{ tableLayout: 'auto', minWidth: '100%' }}>
                                <thead>
                                  <tr className="bg-muted/50">
                                    {getSheetHeaders(sheetData).map((header) => (
                                      <th
                                        key={header}
                                        className="border px-3 py-2 text-left text-sm font-medium text-muted-foreground whitespace-nowrap"
                                        style={{ 
                                          maxWidth: '200px',
                                          overflow: 'hidden',
                                          textOverflow: 'ellipsis'
                                        }}
                                        title={header}
                                      >
                                        {header}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {sheetData.Records.map((row, index) => (
                                    <tr key={`row-${sheetData.Name}-${index}`} className="hover:bg-accent">
                                      {getSheetHeaders(sheetData).map((header) => (
                                        <td
                                          key={header}
                                          className="border px-3 py-2 text-sm"
                                          style={{ 
                                            maxWidth: '200px',
                                            overflow: 'hidden',
                                            wordWrap: 'break-word'
                                          }}
                                          title={String(row[header] || '')}
                                        >
                                          {/* Handle different types of data */}
                                          {typeof row[header] !== 'object' || row[header] === null ? (
                                            <div className="text-muted-foreground">
                                              {String(row[header] || '')}
                                            </div>
                                          ) : (
                                            <pre className="text-xs bg-muted p-1 rounded font-mono whitespace-pre-wrap break-words">
                                              {JSON.stringify(row[header], null, 1)}
                                            </pre>
                                          )}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-sm text-muted-foreground bg-muted/50 rounded-md border border-dashed">
              No expectation data available
            </div>
          )}
        </div>

      </div>
    </div>
  );
}

// Expectation Details Modal Component
interface ExpectationDetailsModalProps {
  expectation: ExpectationResult;
  onClose: () => void;
}

// Run Logs Modal Component
interface RunLogsModalProps {
  run: TestRun;
  onClose: () => void;
}

export function RunLogsModal({ run, onClose }: RunLogsModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-popover rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[80vh] overflow-hidden border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/50">
          <h3 className="text-lg font-semibold ">Run Logs</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-auto max-h-96">
          <div className="space-y-6">
            {/* Run Status Log */}
            <div className="border-b border-gray-200 pb-4">
              <div className="flex items-center space-x-3 mb-3">
                <div className={`w-3 h-3 rounded-full ${
                  run.state === 'SUCCESS' ? 'bg-green-500' :
                  run.state === 'FAILURE' ? 'bg-red-500' :
                  run.state === 'RUNNING' ? 'bg-blue-500' :
                  'bg-yellow-500'
                }`} />
                <span className="text-sm font-medium text-gray-900">Run Status: {run.state}</span>
              </div>
              {run.log && (
                <div className="ml-6 text-sm text-muted-foreground bg-muted p-3 rounded-md font-mono whitespace-pre-wrap">
                  {run.log}
                </div>
              )}
            </div>

            {/* Steps Logs */}
            {run.steps && run.steps.length > 0 && run.steps.map((step, index) => (
              <div key={step.stepId || `step-${index}`} className="border-b border-gray-200 pb-4 last:border-b-0">
                <div className="flex items-center space-x-3 mb-2">
                  <div className={`w-3 h-3 rounded-full ${
                    step.state === 'SUCCESS' ? 'bg-green-500' :
                    step.state === 'FAILURE' ? 'bg-red-500' :
                    step.state === 'RUNNING' ? 'bg-blue-500' :
                    step.state === 'INIT' ? 'bg-muted-foreground' :
                    'bg-yellow-500'
                  }`} />
                  <span className="text-sm font-medium">{step.name || `Step ${step.number}`}</span>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    step.state === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                    step.state === 'FAILURE' ? 'bg-red-100 text-red-800' :
                    step.state === 'RUNNING' ? 'bg-blue-100 text-blue-800' :
                    step.state === 'INIT' ? 'bg-gray-100 text-gray-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>{step.state}</span>
                </div>
                {step.log && (
                  <div className="ml-6 text-sm text-muted-foreground bg-muted p-3 rounded-md font-mono whitespace-pre-wrap">
                    {step.log}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
}

// Run Details Modal Component  
interface RunDetailsModalProps {
  run: TestRun;
  onClose: () => void;
}

export function RunDetailsModal({ run, onClose }: RunDetailsModalProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-popover rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/50">
          <h3 className="text-lg font-semibold ">Run Details</h3>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-auto max-h-96">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Created At</span>
              <span className="text-sm">{formatDate(run.createdAt)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Started At</span>
              <span className="text-sm text-muted-foreground">{run.startedAt ? formatDate(run.startedAt) : 'Not started'}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Finished At</span>
              <span className="text-sm text-muted-foreground">{run.finishedAt ? formatDate(run.finishedAt) : 'Not finished'}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">State</span>
              <span className={`text-sm px-2 py-1 rounded-full text-xs font-medium ${
                run.state === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                run.state === 'FAILURE' ? 'bg-red-100 text-red-800' :
                run.state === 'RUNNING' ? 'bg-blue-100 text-blue-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>{run.state}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Group ID</span>
              <span className="text-sm text-muted-foreground font-mono">{run.groupId}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">External ID</span>
              <span className="text-sm font-medium">{run.externalId}</span>
            </div>
            <div className="flex items-start justify-between py-3">
              <span className="text-sm font-medium text-muted-foreground">Log</span>
              <div className="text-right max-w-md">
                {run.log ? (
                  <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md font-mono whitespace-pre-wrap">
                    {run.log}
                  </div>
                ) : (
                  <span className="text-sm text-muted-foreground italic">No log available</span>
                )}
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}

export function ExpectationDetailsModal({ expectation, onClose }: ExpectationDetailsModalProps) {
  const formatRunOn = (runOn: string) => {
    switch (runOn) {
      case 'FIRST_X_RECORDS':
        return 'First X Records';
      case 'SPECIFIC_X_RECORDS':
        return 'Specific X Records';
      case 'RANDOM_X_RECORDS':
        return 'Random X Records';
      case 'SINGLE_RECORD':
        return 'Single Record';
      default:
        return runOn;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-popover rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] overflow-hidden border" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/50">
          <h3 className="text-lg font-semibold">Expectation Details</h3>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <div className="p-6 overflow-auto max-h-96">
          <div className="space-y-4">
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">External ID</span>
              <span className="text-sm font-medium">#{expectation.externalId}</span>
            </div>
            <div className="flex items-start justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Expectation</span>
              <div className="text-right max-w-md">
                <span className="text-sm">{expectation.expectation}</span>
              </div>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Run On</span>
              <span className="text-sm">{formatRunOn(expectation.runOn)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Record Count</span>
              <span className="text-sm">{expectation.recordCount}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Run State</span>
              <span className={`text-sm px-2 py-1 rounded-full text-xs font-medium ${
                expectation.runState === 'SUCCESS' ? 'bg-green-100 text-green-800' :
                expectation.runState === 'FAILURE' ? 'bg-red-100 text-red-800' :
                expectation.runState === 'RUNNING' ? 'bg-blue-100 text-blue-800' :
                expectation.runState === 'INIT' ? 'bg-gray-100 text-gray-800' :
                'bg-yellow-100 text-yellow-800'
              }`}>{expectation.runState}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b">
              <span className="text-sm font-medium text-muted-foreground">Result Matches</span>
              <span className={`text-sm px-2 py-1 rounded-full text-xs font-medium ${
                expectation.resultMatches ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
              }`}>
                {expectation.resultMatches ? 'Yes' : 'No'}
              </span>
            </div>
            {expectation.foundResult && (
              <div className="flex items-start justify-between py-3 border-b">
                <span className="text-sm font-medium text-muted-foreground">Found Result</span>
                <div className="text-right max-w-md">
                  <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md font-mono whitespace-pre-wrap">
                    {expectation.foundResult}
                  </div>
                </div>
              </div>
            )}
            {expectation.expectedResult && (
              <div className="flex items-start justify-between py-3">
                <span className="text-sm font-medium text-muted-foreground">Expected Result</span>
                <div className="text-right max-w-md">
                  <div className="text-sm text-muted-foreground bg-muted p-3 rounded-md font-mono whitespace-pre-wrap">
                    {expectation.expectedResult}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
